import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const Navbar: React.FC = () => {
    const { isAuthenticated, isAdmin, user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    const isActive = (path: string) => location.pathname === path;

    return (
        <nav className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-white/10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center">
                        <div className="relative">
                            <img
                                src="/logo.png"
                                alt="Coffee Lacoste"
                                className="h-10"
                            />
                            <div className="absolute inset-0 bg-black/40 rounded"></div>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-8">
                        <Link
                            to="/"
                            className={`transition-colors duration-200 ${
                                isActive("/")
                                    ? "text-accent"
                                    : "text-gray-300 hover:text-white"
                            }`}
                        >
                            Accueil
                        </Link>
                        <Link
                            to="/contact"
                            className={`transition-colors duration-200 ${
                                isActive("/contact")
                                    ? "text-accent"
                                    : "text-gray-300 hover:text-white"
                            }`}
                        >
                            Contact
                        </Link>
                        <Link
                            to="/products"
                            className={`transition-colors duration-200 ${
                                isActive("/products")
                                    ? "text-accent"
                                    : "text-gray-300 hover:text-white"
                            }`}
                        >
                            Produits
                        </Link>

                        {isAdmin && (
                            <>
                                <Link
                                    to="/admin"
                                    className={`transition-colors duration-200 ${
                                        isActive("/admin")
                                            ? "text-accent"
                                            : "text-gray-300 hover:text-white"
                                    }`}
                                >
                                    Dashboard
                                </Link>
                                <Link
                                    to="/admin/categories"
                                    className={`transition-colors duration-200 ${
                                        isActive("/admin/categories")
                                            ? "text-accent"
                                            : "text-gray-300 hover:text-white"
                                    }`}
                                >
                                    Categories
                                </Link>
                                <Link
                                    to="/admin/products"
                                    className={`transition-colors duration-200 ${
                                        isActive("/admin/products")
                                            ? "text-accent"
                                            : "text-gray-300 hover:text-white"
                                    }`}
                                >
                                    Produits
                                </Link>
                            </>
                        )}
                    </div>

                    {/* Auth Buttons */}
                    <div className="hidden md:flex items-center space-x-4">
                        {isAuthenticated && (
                            <div className="flex items-center space-x-4">
                                <span className="text-gray-300 text-sm">
                                    {user?.username}
                                    <span className="ml-2 text-accent text-xs">
                                        (Admin)
                                    </span>
                                </span>
                                <button
                                    onClick={handleLogout}
                                    className="btn-secondary text-sm py-1.5 px-4"
                                >
                                    Deconnexion
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Mobile burger */}
                    <div className="md:hidden flex items-center space-x-3">
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="text-gray-300 hover:text-white"
                        >
                        <svg
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            {isMenuOpen ? (
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            ) : (
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 6h16M4 12h16M4 18h16"
                                />
                            )}
                        </svg>
                    </button>
                    </div>
                </div>

                {/* Mobile menu */}
                {isMenuOpen && (
                    <div className="md:hidden py-4 border-t border-white/10">
                        <div className="flex flex-col space-y-4">
                            <Link
                                to="/"
                                className="text-gray-300 hover:text-white"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                Accueil
                            </Link>
                            <Link
                                to="/contact"
                                className="text-gray-300 hover:text-white"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                Contact
                            </Link>
                            <Link
                                to="/products"
                                className="text-gray-300 hover:text-white"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                Produits
                            </Link>

                            {isAdmin && (
                                <>
                                    <Link
                                        to="/admin"
                                        className="text-gray-300 hover:text-white"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        Dashboard
                                    </Link>
                                    <Link
                                        to="/admin/categories"
                                        className="text-gray-300 hover:text-white"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        Categories
                                    </Link>
                                    <Link
                                        to="/admin/products"
                                        className="text-gray-300 hover:text-white"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        Produits
                                    </Link>
                                </>
                            )}

                            {isAuthenticated && (
                                <div className="pt-4 border-t border-white/10">
                                    <button
                                        onClick={handleLogout}
                                        className="btn-secondary w-full"
                                    >
                                        Deconnexion
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </nav>
    );
};

export default Navbar;
